<template>
    <frm-modal-component
        ref="modal"
        :header-description="$t('settings.external-interfaces.online-planner.employees.configuration.description')"
        :loading="saving"
        :save-disabled="loading || !hasChanges"
        cancel-variant="danger"
        class="online-planner-employee-configuration"
        data-testid="online-planner-employee-configuration-modal"
        @save="save"
    >
        <template #header>
            {{ $t('settings.external-interfaces.online-planner.employees.configuration.title', {name: employee.name}) }}
        </template>

        <div v-if="loading" class="online-planner-employees__loading">
            <frm-spinner />
        </div>

        <template v-else>
            <p class="online-planner-employee-configuration__intro">
                {{ $t('settings.external-interfaces.online-planner.employees.configuration.description-intro') }}
            </p>

            <section class="online-planner-employee-configuration__routing">
                <div
                    class="online-planner-employee-configuration__specializations"
                    :class="{'online-planner-employee-configuration__specializations--unavailable': !specializations.length}"
                >
                    <div class="online-planner-employee-configuration__setting">
                        <div>
                            <h3>{{ $t('settings.external-interfaces.online-planner.employees.configuration.use-specializations') }}</h3>
                            <p>{{ $t('settings.external-interfaces.online-planner.employees.configuration.use-specializations-help') }}</p>
                        </div>

                        <div class="form-switch form-switch--large">
                            <input
                                :id="'online-planner-use-specializations-' + employee.id"
                                v-model="useSpecializations"
                                :disabled="!specializations.length"
                                class="form-switch__input"
                                role="switch"
                                type="checkbox"
                            >
                            <label class="form-switch__label" :for="'online-planner-use-specializations-' + employee.id">
                                <span class="form-switch__knob">
                                    <span class="form-switch__dot" />
                                </span>
                                <span class="form-switch__label-text">
                                    {{ $t('settings.external-interfaces.online-planner.employees.configuration.use-specializations') }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <ul v-if="useSpecializations && specializations.length" class="online-planner-employee-configuration__specialization-list">
                        <li
                            v-for="(specialization, index) in specializations"
                            :key="specialization.key"
                            class="online-planner-employee-configuration__specialization"
                        >
                            <strong>{{ specialization.label }}</strong>
                            <div class="form-switch form-switch--large">
                                <input
                                    :id="'online-planner-specialization-' + employee.id + '-' + index"
                                    :checked="!disabledSpecializations.includes(specialization.key)"
                                    class="form-switch__input"
                                    role="switch"
                                    type="checkbox"
                                    @change="toggleSpecialization(specialization.key)"
                                >
                                <label class="form-switch__label" :for="'online-planner-specialization-' + employee.id + '-' + index">
                                    <span class="form-switch__knob">
                                        <span class="form-switch__dot" />
                                    </span>
                                    <span class="form-switch__label-text">{{ specialization.label }}</span>
                                </label>
                            </div>
                        </li>
                    </ul>

                    <div v-if="!specializations.length" class="online-planner-employee-configuration__specializations-help">
                        <p>{{ $t('settings.external-interfaces.online-planner.employees.configuration.specializations-unavailable') }}</p>

                        <template v-if="employee.employee_edit_url">
                            <div class="online-planner-employee-configuration__specialization-actions">
                                <a
                                    :href="employee.employee_edit_url"
                                    class="online-planner-employee-configuration__specialization-link"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    @click="waitForSpecializationUpdate"
                                >
                                    {{ $t('settings.external-interfaces.online-planner.employees.configuration.edit-agb-registration') }}
                                    <frm-icon name="external-link" />
                                </a>
                                <button
                                    v-if="awaitingSpecializationRefresh"
                                    :disabled="refreshingSpecializations"
                                    class="online-planner-employee-configuration__specialization-refresh"
                                    type="button"
                                    @click="refreshSpecializations"
                                >
                                    {{ $t(`settings.external-interfaces.online-planner.employees.configuration.${refreshingSpecializations ? 'checking-specializations' : 'check-specializations-again'}`) }}
                                </button>
                            </div>
                            <small v-if="awaitingSpecializationRefresh">
                                {{ $t('settings.external-interfaces.online-planner.employees.configuration.specializations-auto-refresh') }}
                            </small>
                        </template>
                        <p v-else class="online-planner-employee-configuration__specializations-admin-help">
                            {{ $t('settings.external-interfaces.online-planner.employees.configuration.specializations-admin-help') }}
                        </p>
                    </div>
                </div>
            </section>

            <section class="online-planner-employee-configuration__areas">
                <h3>{{ $t('settings.external-interfaces.online-planner.employees.configuration.complaint-areas') }}</h3>
                <p>{{ $t('settings.external-interfaces.online-planner.employees.configuration.complaint-areas-help') }}</p>

                <div
                    v-if="selectedComplaintAreas.length || newComplaintAreaNames.length"
                    class="online-planner-employee-configuration__chips online-planner-employee-configuration__chips--selected"
                >
                    <button
                        v-for="complaintArea in selectedComplaintAreas"
                        :key="complaintArea.id"
                        class="online-planner-employee-configuration__chip online-planner-employee-configuration__chip--selected"
                        type="button"
                        @click="removeComplaintArea(complaintArea.id)"
                    >
                        {{ complaintArea.name }}
                        <span aria-hidden="true">&times;</span>
                    </button>
                    <button
                        v-for="name in newComplaintAreaNames"
                        :key="'new-' + name"
                        class="online-planner-employee-configuration__chip online-planner-employee-configuration__chip--selected"
                        type="button"
                        @click="removeNewComplaintArea(name)"
                    >
                        {{ name }}
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="online-planner-employee-configuration__add">
                    <input
                        v-model.trim="newComplaintArea"
                        :placeholder="$t('settings.external-interfaces.online-planner.employees.configuration.complaint-area-placeholder')"
                        class="form-input"
                        maxlength="255"
                        type="text"
                        @keydown.enter.prevent="addComplaintArea"
                    >
                    <button
                        :disabled="!newComplaintArea"
                        class="button button--secondary"
                        type="button"
                        @click="addComplaintArea"
                    >
                        {{ $t('settings.external-interfaces.online-planner.employees.configuration.add') }}
                    </button>
                </div>
                <small v-if="complaintAreaError" class="online-planner-employee-configuration__error">
                    {{ complaintAreaError }}
                </small>

                <template v-if="availableComplaintAreas.length">
                    <strong class="online-planner-employee-configuration__existing">
                        {{ $t('settings.external-interfaces.online-planner.employees.configuration.existing-complaint-areas') }}
                    </strong>
                    <div class="online-planner-employee-configuration__chips">
                        <button
                            v-for="complaintArea in availableComplaintAreas"
                            :key="complaintArea.id"
                            class="online-planner-employee-configuration__chip online-planner-employee-configuration__chip--available"
                            type="button"
                            @click="selectComplaintArea(complaintArea.id)"
                        >
                            + {{ complaintArea.name }}
                        </button>
                    </div>
                </template>
            </section>
        </template>
    </frm-modal-component>
</template>

<script>
import axios from 'axios';
import HandlesErrorResponses from '../../mixins/HandlesErrorResponses';

export default {
    mixins: [HandlesErrorResponses],

    props: {
        employee: {
            type: Object,
            required: true,
        },

        url: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            complaintAreas: [],
            complaintAreaIds: [],
            initialComplaintAreaIds: [],
            newComplaintAreaNames: [],
            specializations: [],
            disabledSpecializations: [],
            initialDisabledSpecializations: [],
            useSpecializations: false,
            initialUseSpecializations: false,
            newComplaintArea: '',
            complaintAreaError: '',
            loading: true,
            saving: false,
            awaitingSpecializationRefresh: false,
            refreshingSpecializations: false,
        };
    },

    computed: {
        selectedComplaintAreas() {
            return this.complaintAreaIds
                .map(id => this.complaintAreas.find(complaintArea => complaintArea.id === id))
                .filter(Boolean);
        },

        availableComplaintAreas() {
            const query = this.normalizeComplaintAreaName(this.newComplaintArea);

            return this.complaintAreas.filter(complaintArea => {
                const isAvailable = complaintArea.active !== false &&
                    !this.complaintAreaIds.includes(complaintArea.id);

                return isAvailable && (!query || this.normalizeComplaintAreaName(complaintArea.name).includes(query));
            });
        },

        hasChanges() {
            return this.useSpecializations !== this.initialUseSpecializations ||
                this.disabledSpecializations.length !== this.initialDisabledSpecializations.length ||
                this.disabledSpecializations.some(key => !this.initialDisabledSpecializations.includes(key)) ||
                this.newComplaintAreaNames.length > 0 ||
                this.complaintAreaIds.length !== this.initialComplaintAreaIds.length ||
                this.complaintAreaIds.some(id => !this.initialComplaintAreaIds.includes(id));
        },
    },

    mounted() {
        window.addEventListener('focus', this.refreshSpecializationsAfterExternalEdit);

        if (this.employee.complaint_area_ids && this.employee.complaint_areas) {
            this.setConfiguration(this.employee);
            this.loading = false;

            return;
        }

        this.fetchConfiguration();
    },

    beforeDestroy() {
        window.removeEventListener('focus', this.refreshSpecializationsAfterExternalEdit);
    },

    methods: {
        normalizeComplaintAreaName(value) {
            return value
                .trim()
                .toLocaleLowerCase('nl-NL')
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '');
        },

        sameName(a, b) {
            return this.normalizeComplaintAreaName(a) === this.normalizeComplaintAreaName(b);
        },

        async requestConfiguration() {
            const { data, complaint_areas: complaintAreas } = (await axios.get(this.url)).data;

            return { ...data, complaint_areas: complaintAreas };
        },

        async fetchConfiguration() {
            this.loading = true;

            try {
                this.setConfiguration(await this.requestConfiguration());
            } catch (error) {
                await this.handleErrorResponse(error);
                this.$refs.modal.closeModal();
            } finally {
                this.loading = false;
            }
        },

        waitForSpecializationUpdate() {
            this.awaitingSpecializationRefresh = true;
        },

        refreshSpecializationsAfterExternalEdit() {
            if (this.awaitingSpecializationRefresh && !this.specializations.length) {
                this.refreshSpecializations();
            }
        },

        async refreshSpecializations() {
            if (this.refreshingSpecializations) {
                return;
            }

            this.refreshingSpecializations = true;

            try {
                const configuration = await this.requestConfiguration();

                this.specializations = [...(configuration.specializations || [])];
                this.useSpecializations = configuration.use_specializations_for_patient_portal === true && this.specializations.length > 0;
                this.awaitingSpecializationRefresh = !this.specializations.length;

                this.$emit('configuration-refreshed', {
                    ...configuration,
                    specializations: [...this.specializations],
                    use_specializations_for_patient_portal: this.useSpecializations,
                });
            } catch (error) {
                await this.handleErrorResponse(error);
            } finally {
                this.refreshingSpecializations = false;
            }
        },

        setConfiguration(configuration) {
            this.complaintAreas = (configuration.complaint_areas || []).map(complaintArea => ({ ...complaintArea }));
            this.complaintAreaIds = [...(configuration.complaint_area_ids || [])];
            this.initialComplaintAreaIds = [...this.complaintAreaIds];
            this.newComplaintAreaNames = [];
            this.specializations = [...(configuration.specializations || [])];
            this.useSpecializations = configuration.use_specializations_for_patient_portal === true && this.specializations.length > 0;
            this.initialUseSpecializations = this.useSpecializations;
            this.disabledSpecializations = (configuration.disabled_specializations || [])
                .filter(key => this.specializations.some(specialization => specialization.key === key));
            this.initialDisabledSpecializations = [...this.disabledSpecializations];
        },

        toggleSpecialization(key) {
            this.disabledSpecializations = this.disabledSpecializations.includes(key)
                ? this.disabledSpecializations.filter(disabledKey => disabledKey !== key)
                : [...this.disabledSpecializations, key];
        },

        selectComplaintArea(id) {
            if (!this.complaintAreaIds.includes(id)) {
                this.complaintAreaIds.push(id);
            }

            this.newComplaintArea = '';
            this.complaintAreaError = '';
        },

        removeComplaintArea(id) {
            this.complaintAreaIds = this.complaintAreaIds.filter(complaintAreaId => complaintAreaId !== id);
        },

        removeNewComplaintArea(name) {
            this.newComplaintAreaNames = this.newComplaintAreaNames.filter(newName => newName !== name);
        },

        addComplaintArea() {
            const name = this.newComplaintArea.trim();

            if (!name) {
                return;
            }

            const existingComplaintArea = this.complaintAreas.find(complaintArea =>
                complaintArea.active !== false && this.sameName(complaintArea.name, name)
            );

            if (existingComplaintArea || this.newComplaintAreaNames.some(newName => this.sameName(newName, name))) {
                if (existingComplaintArea) {
                    this.selectComplaintArea(existingComplaintArea.id);
                }

                this.complaintAreaError = this.$t('settings.external-interfaces.online-planner.employees.configuration.complaint-area-exists');
                this.newComplaintArea = '';

                return;
            }

            this.newComplaintAreaNames.push(name);
            this.newComplaintArea = '';
            this.complaintAreaError = '';
        },

        async save() {
            this.saving = true;

            try {
                const attributes = {
                    complaint_area_ids: this.complaintAreaIds,
                    new_complaint_areas: this.newComplaintAreaNames,
                    disabled_specializations: this.disabledSpecializations,
                };

                if (this.useSpecializations !== this.initialUseSpecializations) {
                    attributes.use_specializations_for_patient_portal = this.useSpecializations;
                }

                const { data, complaint_areas: complaintAreas } = (await axios.patch(this.url, attributes)).data;

                this.$refs.modal.closeModal({ ...data, complaint_areas: complaintAreas });
            } catch (error) {
                await this.handleErrorResponse(error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
